import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  CheckCircle2, Circle, ArrowRight, RefreshCw,
  CalendarClock, FileSpreadsheet, ScrollText, ClipboardCheck,
} from "lucide-react";

/**
 * Monthly Payroll Cycle Progress Card — Dashboard tile
 * Shows the 4-stage workflow snapshot for the selected month:
 *   Attendance Finalized → Leaves Approved → Payroll Generated → Slips Approved.
 * Data source: GET /api/dashboard/payroll-cycle?month=YYYY-MM-DD
 */

const STAGE_ICONS = {
  attendance_finalized: CalendarClock,
  leaves_approved: ScrollText,
  payroll_generated: FileSpreadsheet,
  slips_approved: ClipboardCheck,
};

const STAGE_LINKS = {
  attendance_finalized: "/attendance/monthly",
  leaves_approved: "/leaves",
  payroll_generated: "/payroll",
  slips_approved: "/payroll",
};

// Build [current, prev-1, ..., prev-11] months for the selector.
function buildMonthOptions() {
  const opts = [];
  const now = new Date();
  const y0 = now.getFullYear();
  const m0 = now.getMonth();
  for (let i = 0; i < 12; i++) {
    const d = new Date(y0, m0 - i, 1);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
    const label = d.toLocaleString("en-IN", { month: "short", year: "numeric" });
    opts.push({ iso, label });
  }
  return opts;
}

export default function PayrollCycleWidget() {
  const monthOpts = useMemo(buildMonthOptions, []);
  const [month, setMonth] = useState(monthOpts[0].iso);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get(`/dashboard/payroll-cycle?month=${month}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [month]);

  const percent = data?.percent ?? 0;
  const stages = data?.stages || [];

  return (
    <Card
      className="border-slate-200 shadow-sm"
      data-testid="payroll-cycle-widget"
    >
      <CardContent className="p-4 sm:p-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4 brand-text" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">
                Monthly Payroll Cycle
              </div>
              <div
                className="font-semibold text-slate-900 text-sm"
                style={{ fontFamily: "Chivo" }}
              >
                Workflow progress · {data?.month_label || "—"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger
                className="w-[140px] h-8 text-xs"
                data-testid="payroll-cycle-month-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthOpts.map((o) => (
                  <SelectItem
                    key={o.iso}
                    value={o.iso}
                    data-testid={`payroll-cycle-month-${o.iso}`}
                  >
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="payroll-cycle-refresh-btn"
              className="h-8 text-xs"
            >
              <RefreshCw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Overall progress bar */}
        <div className="mb-5" data-testid="payroll-cycle-overall">
          <div className="flex items-baseline justify-between mb-1.5">
            <span className="text-[11px] uppercase tracking-wider text-slate-500">
              Overall completion
            </span>
            <span
              className="text-lg font-semibold text-slate-900"
              style={{ fontFamily: "Chivo" }}
              data-testid="payroll-cycle-percent"
            >
              {percent}%
            </span>
          </div>
          <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full brand-bg transition-all"
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>

        {/* Stages grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {stages.map((s, idx) => {
            const Icon = STAGE_ICONS[s.key] || Circle;
            const StatusIcon = s.complete ? CheckCircle2 : Circle;
            const to = STAGE_LINKS[s.key] || "/";
            return (
              <Link
                key={s.key}
                to={to}
                data-testid={`payroll-cycle-stage-${s.key}`}
                className={`group relative rounded-lg border p-3 hover:shadow-sm transition-all ${
                  s.complete
                    ? "border-green-200 bg-green-50/50"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div
                    className={`w-8 h-8 rounded-md flex items-center justify-center ${
                      s.complete
                        ? "bg-green-100 text-green-700"
                        : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                  </div>
                  <StatusIcon
                    className={`h-4 w-4 ${
                      s.complete ? "text-green-600" : "text-slate-300"
                    }`}
                  />
                </div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">
                  Step {idx + 1}
                </div>
                <div className="text-sm font-semibold text-slate-900 leading-tight">
                  {s.label}
                </div>
                <div className="mt-1.5 text-xs text-slate-600">
                  {s.total > 0 ? (
                    <span>
                      <span className="font-medium">{s.done}</span>
                      <span className="text-slate-400"> / {s.total}</span>
                      <span className="text-slate-500 ml-1">
                        {s.key === "leaves_approved" ? "resolved" : "units"}
                      </span>
                    </span>
                  ) : (
                    <span className="text-slate-400 italic">
                      {s.key === "leaves_approved" ? "no requests" : "no data"}
                    </span>
                  )}
                </div>
                <ArrowRight className="h-3 w-3 text-slate-300 group-hover:text-slate-500 absolute bottom-2 right-2" />
              </Link>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
